import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {WB_CompanyRow, WB_LoaderOperationRow, WB_MasterDataSetRow} from "../PA.Core/PAdbSchemaRows";
import {IwbLoaderOperation} from "../PA.Core/PAdbSchemaRowsIF";
import {esWB_Company, esWB_Loader_Operation, esWB_MasterData_Set} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {LoaderOperationBOValidations} from "./LoaderOperationBO.Validations";


export class LoaderOperationBO
    implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_LoaderOperation: WB_LoaderOperationRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Loader Operation'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_LoaderOperation = undefined;
    }

    get WB_LoaderOperation(): WB_LoaderOperationRow {
        return this.drWB_LoaderOperation;
    }
    //</editor-fold>

    //<editor-fold desc="IBusinessObject" operations>

    LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Loader Operation");
        let internalOnComplete = self._OnLoadComplete = (async () => {
            try {
                self.LoadOptions = options || {};
                if ("dbContext" in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else
                    self.dbContext = new PAdbContext();

                if (this.dbContext == undefined) {
                    let sError = "Unable to determine dbContext for new Loader Operation";
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                //Create record using dbContext
                let newLoaderOperationRow: WB_LoaderOperationRow = <WB_LoaderOperationRow>await self.dbContext.createRecordWithKey(esWB_Loader_Operation)

                newLoaderOperationRow.setDefaults();
                self.drWB_LoaderOperation = newLoaderOperationRow;
                //add this to all the BOs
                self.dbContext.add(esWB_Loader_Operation, newLoaderOperationRow);
                self.Identity.setAll(self.WB_LoaderOperation.cif.WB_LOADEROPERATION_ID, self.WB_LoaderOperation.cif.LOADEROPERATION_ID, self.WB_LoaderOperation.cif.LOADEROPERATION_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_LoaderOperation, "WB_LOADEROPERATION_ID", "LOADEROPERATION_ID", "LOADEROPERATION_NAME");

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined;
                await self.applyCreateOptions()
                self.Identity.setAll(self.WB_LoaderOperation.cif.WB_LOADEROPERATION_ID, self.WB_LoaderOperation.cif.LOADEROPERATION_ID, self.WB_LoaderOperation.cif.LOADEROPERATION_NAME);

                return self;
            } catch (err) {

                self.LoadProgressProvider.exception(err)
                return self
            }
        })()

        return internalOnComplete
    }

    async applyCreateOptions() {
        const self = this

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId
        if (!_.isEmpty(sId))
            self.WB_LoaderOperation.cif.LOADEROPERATION_ID = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_LoaderOperation.cif.LOADEROPERATION_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_LoaderOperation.WB_MasterDataSetRow = mds
                self.WB_LoaderOperation.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

        // Set Company
        let wbCompany = self.CreateOptions?.wbCompanyId || self.LoadOptions?.wbCompanyId
        if (_.isNumber(wbMasterDataSet)) {
            let company: WB_CompanyRow = <any>await self.dbContext.find(esWB_Company, wbCompany)
            if (company) {
                self.WB_LoaderOperation.WB_CompanyRow = company
                self.WB_LoaderOperation.cif.WB_COMPANY_ID = wbCompany
            }
        }

    }

    Load(anId: number | number, options?: any): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject)=>{

            if(self.bIsLoaded) {
                let sError = "Loader Operation already loaded";
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if(self.dbContext == undefined)
                self.dbContext = new PAdbContext();

            if(this.dbContext == undefined){
                let sError = "Unable to determine dbContext for new Loader Operation";
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            //Added for record resolver
            if("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = LoaderOperationBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = LoaderOperationBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function() {
                    let aLoaderOperationRow: any = recordResolver.get(esWB_Loader_Operation, anId);

                    if(_.isEmpty(aLoaderOperationRow)){
                        self._OnLoadComplete = undefined;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_LoaderOperation = aLoaderOperationRow;
                    self.Identity.setAll(self.WB_LoaderOperation.cif.WB_LOADEROPERATION_ID, self.WB_LoaderOperation.cif.LOADEROPERATION_ID, self.WB_LoaderOperation.cif.LOADEROPERATION_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_LoaderOperation, "WB_LOADEROPERATION_ID", "LOADEROPERATION_ID", "LOADEROPERATION_NAME");

                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err)=>{
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                })
        });
        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this;

        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_LoaderOperation.cif.LOADEROPERATION_NAME + "(" + self.WB_LoaderOperation.cif.LOADEROPERATION_ID + ")");
        return (async () => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            try {

                if (self.dbContext == undefined)
                    throw "Unable to determine dbContext for to Validate Loader Operation.";


                await LoaderOperationBOValidations.Validation(self)

                if(self.ValidateMessageProvider.countMessages(MessageType.Error) > 0){
                    self._bIsValidated = false;
                } else {
                    self._bIsValidated = true;
                }
                self.ValidateProgressProvider.finish();


            } catch (err) {
                self._bIsValidated = false;
                self.ValidateProgressProvider.exception(err);
            } finally {
                return self
            }

        })();
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbLoaderOperation = <IwbLoaderOperation>self.drWB_LoaderOperation;
        this.SaveProgressProvider.start("Saving " + wbLoaderOperation.LOADEROPERATION_NAME + "(" + wbLoaderOperation.LOADEROPERATION_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject)=>{
            //Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == undefined) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Loader Operation.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }
            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if(bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            let bDone = false

            self.Validate({})
                .then((ignore) => {
                    if(!self.bIsValidated){
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return
                    }
                })
                .then((ignore) => {
                    if (bDone) return
                    return saveFunction(esWB_Loader_Operation, self.drWB_LoaderOperation, {})
                })
                .then((ignore) => {
                    if (bDone) return
                    let tasks: Promise<any>[] = []
                    tasks.push(saveFunction(esWB_MasterData_Set, self.drWB_LoaderOperation.WB_MasterDataSetRow, {}))
                    tasks.push(saveFunction(esWB_Company, self.drWB_LoaderOperation.WB_CompanyRow, {}))

                    return Promise.all(tasks)
                })
                .then((ignored)=>{
                    if (bDone) return
                    if(bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        })
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self);
                })
                .catch((err)=>{
                    self.SaveProgressProvider.exception(err);
                    reject(self)
                })
        })
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbLoaderOperation = <IwbLoaderOperation>self.drWB_LoaderOperation;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbLoaderOperation.LOADEROPERATION_NAME + "(" + wbLoaderOperation.LOADEROPERATION_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject)=>{
            // 1. Validate if we can delete the Loader Operation

            // 2. If Valid, mark all the records for deletion.
            if(self.DeleteMessageProvider.countMessages(MessageType.Error) > 0){
                // Don't delete..
                this.DeleteProgressProvider.finish();
                resolve(self)
                return;
            }

            self.drWB_LoaderOperation.markDeleted()
            if(bSaveNow){
                self
                    .SaveUsing(false)
                    .then(ignore => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch(err => {
                        this.DeleteProgressProvider.exception(err);
                        reject(self);
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        })
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const loader_operation_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, loader_operation_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void>{
        return new Promise<void>((resolve, reject)=>{
            recordResolver.findAllWithRelationships(esWB_Loader_Operation, ids)
                .then((result)=>{
                    resolve();
                })
                .catch((error)=>{
                    reject(error);
                })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass( LoaderOperationBO , "LoaderOperationBO", "Loader Operation")